import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { AuthProvider } from '@/auth/AuthContext';
import { RequireAuth } from '@/auth/RequireAuth';
import { WorkspaceProvider } from '@/workspace/WorkspaceContext';
import { AppShell } from '@/components/layout/AppShell';
import { Login } from '@/pages/Login';
import { Overview } from '@/pages/Overview';
import { System } from '@/pages/System';
import { Tools } from '@/pages/Tools';
import { Organization } from '@/pages/Organization';
import { Audit } from '@/pages/Audit';
import { Feedback } from '@/pages/Feedback';
import { Proposals } from '@/pages/Proposals';
import { RAG } from '@/pages/RAG';
import { Chat } from '@/pages/Chat';
import { Widget } from '@/pages/Widget';
import { Mcp } from '@/pages/Mcp';
import { GoogleChat } from '@/pages/GoogleChat';
import { Cache } from '@/pages/Cache';
import { Integrations } from '@/pages/Integrations';
import { NotFound } from '@/pages/NotFound';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        const status = (error as { response?: { status?: number } } | null)?.response?.status;
        if (status === 401 || status === 403 || status === 404) return false;
        return failureCount < 2;
      },
      staleTime: 5_000,
      refetchOnWindowFocus: false,
    },
  },
});

export function App(): JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <WorkspaceProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route
                element={
                  <RequireAuth>
                    <AppShell />
                  </RequireAuth>
                }
              >
                <Route index element={<Overview />} />
                <Route path="organization" element={<Organization />} />
                <Route path="integrations" element={<Integrations />} />
                <Route path="system" element={<System />} />
                <Route path="tools" element={<Tools />} />
                <Route path="audit" element={<Audit />} />
                <Route path="feedback" element={<Feedback />} />
                <Route path="proposals" element={<Proposals />} />
                <Route path="rag" element={<RAG />} />
                <Route path="chat" element={<Chat />} />
                <Route path="widget" element={<Widget />} />
                <Route path="mcp" element={<Mcp />} />
                <Route path="google-chat" element={<GoogleChat />} />
                <Route path="cache" element={<Cache />} />
                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
          </BrowserRouter>
          <Toaster position="top-right" richColors closeButton />
        </WorkspaceProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
